import api from 'apis/restaurant-api/api';
import { RestaurantApiResults } from 'apis/restaurant-api/types';
import { UiLanguageKey } from 'locale/languages';
import type { VenueRole } from 'modules/access-control';

import {
  AccessDescription,
  DeliveryProvider,
  ExceptionalOpeningTimesAttrs,
  PosIntegrationStatus,
  Venue,
  VenuePatchData,
  MerchantVenuesResponseV3,
  Client,
  Role,
  VenueDataFromAPI,
} from './types';

export const fetchVenues = () =>
  api.get<RestaurantApiResults<Venue>>('/v1/merchant-admin/venues').then(response => response.data.results);

export const fetchMerchantVenuesV3 = async (id: string, pageSize: number, pageToken: string) => {
  const queryParams = `page_size=${pageSize}&page_token=${pageToken}`;

  const response = await api.get<MerchantVenuesResponseV3>(`/v3/merchant-admin/merchants/${id}/venues?${queryParams}`);
  return response.data;
};

export const fetchVenue = (venueIdOrSlug: string) =>
  api.get<Venue>(`/v1/merchant-admin/venues/${venueIdOrSlug}`).then(response => response.data);

export const patchVenue = (venueId: string, venueData: VenuePatchData) =>
  api.patch<Venue>(`/v1/merchant-admin/venues/${venueId}`, venueData).then(response => response.data);

export const editExceptionalOpeningTimes = (venueId: string, data: ExceptionalOpeningTimesAttrs[]) =>
  api.put(`/v1/merchant-admin/venues/${venueId}/exceptional-opening-times`, data).then(response => response.data);

export const fetchVenueDeliveryProvider = (venueId: string) =>
  api.get<DeliveryProvider>(`/v1/merchant-admin/venues/${venueId}/delivery-provider`).then(response => response.data);

export const fetchVenuePosIntegrationStatus = (venueId: string) =>
  api.get<PosIntegrationStatus>(`/v1/merchant-admin/venues/${venueId}/pos-integrated`).then(response => response.data);

export const fetchVenueAccess = (venueId: string) =>
  api
    .get<{ results: AccessDescription[] }>(`/v1/merchant-admin/venues/${venueId}/access`)
    .then(response => response.data.results);

export const deleteVenueAccess = (venueId: string, userEmail: string, userId: string | undefined) =>
  api.delete(`/v1/merchant-admin/venues/${venueId}/access`, {
    data: {
      id: userId,
      email: userEmail,
    },
  });

export const grantTentativeVenueAccess = (
  venueId: string,
  email: string,
  role: VenueRole,
  preferredLocale: UiLanguageKey,
) =>
  api.put(`/v1/merchant-admin/venues/${venueId}/access`, {
    email,
    role,
    preferred_locale: preferredLocale,
  });

export const grantVenueAccess = (venueId: string, userId: string, role: string) =>
  api.put(`/v1/merchant-admin/venues/${venueId}/access`, {
    id: userId,
    role,
  });

export const fetchMerchantAppLogins = (venueId: string) =>
  api.get<{ clients: Client[] }>(`/v2/merchant-admin/venues/${venueId}/merchant-app-logins`).then(res => res.data);

export const createMerchantAppLogin = (venueId: string, { login, role }: { login: string; role?: Role }) =>
  api
    .post<{
      login: string;
      password: string;
      merchant_app_logins: string[];
      role: Role;
    }>(`/v1/merchant-admin/venues/${venueId}/merchant-app-logins`, { login, role })
    .then(res => res.data);

export const updateMerchantAppLogin = (venueId: string, login: string, role: Role) =>
  api
    .post<{
      login: string;
      password: string;
      merchant_app_logins: string[];
      role: Role;
    }>(`/v1/merchant-admin/venues/${venueId}/merchant-app-logins/${login}/change-role`, { role })
    .then(res => res.data);

export const resetMerchantAppLoginPassword = (
  venueId: string,
  login: string,
): Promise<{
  login: string;
  password: string;
  merchant_app_logins: string[];
}> =>
  api
    .post<{
      login: string;
      password: string;
      merchant_app_logins: string[];
      role: Role;
    }>(`/v1/merchant-admin/venues/${venueId}/merchant-app-logins/${encodeURIComponent(login)}/reset-password`)
    .then(res => res.data);

export const deleteMerchantAppLogin = (venueId: string, login: string) =>
  api.delete(`/v1/merchant-admin/venues/${venueId}/merchant-app-logins/${encodeURIComponent(login)}`);

export const fetchVenueAvailabilityStatuses = (
  data: { venueIds: string[] },
  locale: string,
): Promise<VenueDataFromAPI[]> =>
  api
    .post('/v2/merchant-admin/venue_availability_statuses', data, {
      headers: {
        'Accept-Language': locale,
      },
    })
    .then(res => res.data.results);
